/**
 * Client half of the Review screen (admin only).
 *
 * Every call goes to `POST /api/recipes/import` with an `action`, like publish:
 * the server checks the admin allowlist, reads the recipe where the row lives
 * (`scope`: the household's, the cook's own, or the catalogue), and writes the
 * tags and the `metadataLabels` row with the Admin SDK. The app never writes a
 * label itself, so `metadataLabels` needs no client rule — and a 200 without
 * `success: true` is a failure (`requireSuccess`), because Expo answers 200 +
 * HTML for an `/api` path it does not know.
 */
import { apiPost } from './apiClient';
import { trackEvent } from './analyticsService';
import { uploadRecipeImage } from './photoImport/uploadRecipeImage';
import type { LoopStats } from '../utils/recipeMetadataScoring';
import type { ReviewPage, ReviewPageRequest, ReviewRow } from '../utils/recipeReviewQueue';
import type {
  MetadataDecisions,
  MetadataSuggestion,
  ReviewField,
  ReviewScope,
} from '../types/recipeMetadata';

/** Mirrors MAX_SUGGEST_PER_CALL on the server. */
export const SUGGEST_BATCH_SIZE = 4;

const post = (body: Record<string, unknown>): Promise<any> =>
  apiPost('/api/recipes/import', body, { requireSuccess: true });

export interface ReviewTarget {
  recipeId: string;
  scope: ReviewScope;
}

const targetOf = (row: Pick<ReviewRow, 'id' | 'scope'>): ReviewTarget => ({ recipeId: row.id, scope: row.scope });

export async function fetchReviewPage(request: ReviewPageRequest): Promise<ReviewPage> {
  const data = await post({ action: 'review-queue', ...request });
  return { counts: data.counts, rows: data.rows ?? [], nextOffset: data.nextOffset ?? null };
}

export async function fetchLoopStats(): Promise<LoopStats> {
  return (await post({ action: 'metadata-stats' })).stats;
}

/** Suggestions for these rows, keyed by recipe id. Rows the server could not judge are simply absent. */
export async function requestSuggestions(
  rows: Array<Pick<ReviewRow, 'id' | 'scope'>>,
): Promise<Record<string, MetadataSuggestion>> {
  const out: Record<string, MetadataSuggestion> = {};
  for (let i = 0; i < rows.length; i += SUGGEST_BATCH_SIZE) {
    const data = await post({ action: 'suggest-metadata', items: rows.slice(i, i + SUGGEST_BATCH_SIZE).map(targetOf) });
    Object.assign(out, data.suggestions ?? {});
  }
  return out;
}

export interface ReviewResult {
  /** The fields the server wrote, to patch the row without a reload. */
  update: Record<string, unknown>;
  outcomes: Partial<Record<ReviewField, string>>;
}

/**
 * One person's answer: a field picked from its list, or Accept (`approve` on
 * a new import moves it to Ready to publish). `timeTyped` says the time was
 * typed in the Time editor, which is what lets it set Préparation.
 */
export async function submitReview(
  row: Pick<ReviewRow, 'id' | 'scope' | 'source' | 'metadataSuggestion'>,
  decisions: MetadataDecisions,
  options: { approve?: boolean; timeTyped?: boolean } = {},
): Promise<ReviewResult> {
  const approve = !!options.approve && row.source === 'import';
  const data = await post({
    action: 'review-metadata', ...targetOf(row), decisions, reviewMode: 'single', approve,
    ...(options.timeTyped && { timeTyped: true }),
  });
  const outcomes: ReviewResult['outcomes'] = data.outcomes ?? {};
  const suggestion = row.metadataSuggestion;
  // The live measure of the classifier: accept vs correct, per field, per
  // prompt version. The eval measures the same thing offline.
  if (Object.keys(outcomes).length > 0) {
    trackEvent('recipe_metadata_reviewed', {
      recipe_id: row.id,
      review_mode: 'single',
      scope: row.scope,
      source: row.source,
      prompt_version: suggestion?.promptVersion ?? null,
      few_shot_count: suggestion?.fewShotCount ?? null,
      image_seen: suggestion?.imageSeen ?? null,
      checks: suggestion?.checks.map((c) => c.code) ?? [],
      ...Object.fromEntries(
        Object.entries(outcomes).flatMap(([key, outcome]) => {
          const field = key as ReviewField;
          const s = suggestion?.[field];
          // A time is reported by its total, so PostHog can chart the miss.
          const flat = (v: unknown) => (v && typeof v === 'object' ? (v as { total: number }).total : v ?? null);
          return [
            [`${field}_outcome`, outcome],
            [`${field}_value`, flat(decisions[field])],
            [`${field}_suggested`, flat(s?.value)],
            [`${field}_confidence`, s?.confidence ?? null],
          ];
        }),
      ),
    });
  }
  if (approve) trackEvent('recipe_review_approved', { recipe_id: row.id, mode: 'single' });
  return { update: data.update ?? {}, outcomes };
}

export interface GlanceResult {
  confirmed: Array<{ id: string; update: Record<string, unknown> }>;
  skipped: Array<{ id: string; reason: string }>;
}

/** "These N look right": the rows on screen, confirmed at a glance. */
export async function glanceConfirm(rows: Array<Pick<ReviewRow, 'id' | 'scope' | 'source'>>): Promise<GlanceResult> {
  const data = await post({ action: 'glance-confirm', items: rows.map(targetOf) });
  const result: GlanceResult = { confirmed: data.confirmed ?? [], skipped: data.skipped ?? [] };
  trackEvent('recipe_review_glance_confirmed', {
    shown: rows.length,
    confirmed: result.confirmed.length,
    skipped: result.skipped.length,
    imports: rows.filter((r) => r.source === 'import').length,
  });
  return result;
}

/** Uploads a photo and puts it on the recipe where the row lives. Null when the upload failed. */
export async function addPhoto(row: Pick<ReviewRow, 'id' | 'scope'>, imageUri: string, userId: string): Promise<string | null> {
  const url = await uploadRecipeImage(imageUri, userId, `review-${row.id}-${Date.now()}`);
  if (!url) return null;
  await post({ action: 'fix-content', ...targetOf(row), image: url });
  return url;
}
